/**
 * Crew queries and mutations, and the certificate upload machine.
 *
 * Upload is the same three calls as a project document — presign, PUT to S3,
 * complete — with a fourth the documents path does not have: `confirm`. The
 * third returns *suggestions*, and until the user accepts them the certificate
 * counts for nothing. That is deliberate; see
 * `project_intelligence/document-ai/0002-certificate-extraction-is-a-suggestion.md`.
 */
import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddCrewMemberInput,
  CertificateSuggestion,
  ConfirmCertificateInput,
  CrewPeriod,
  CrewRoster,
  DocType,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { putWithProgress } from "@/lib/upload";
import { UploadRefused, isRefusal, uploadFailure } from "@/lib/upload-failure";
import { documentRefusal } from "@/features/project/documents-api";

export const crewKeys = {
  roster: (projectId: string) => ["project", projectId, "crew"] as const,
  periods: (projectId: string) => ["project", projectId, "crew", "periods"] as const,
};

export function useRoster(projectId: string | undefined) {
  return useQuery({
    queryKey: crewKeys.roster(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<CrewRoster> => {
      const res = await api.crew.roster({ params: { id: projectId! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function usePeriods(projectId: string | undefined) {
  return useQuery({
    queryKey: crewKeys.periods(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<CrewPeriod[]> => {
      const res = await api.crew.periods({ params: { id: projectId! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.periods;
    },
  });
}

/**
 * Anything that changes who is on site invalidates both lists.
 *
 * Offboarding shows up in the roster *and* closes a period in Reports, and a
 * user who exports the audit trail straight after marking someone off-site
 * would otherwise get a file that still says they are here.
 */
function useCrewMutation<TVars, TData>(
  projectId: string | undefined,
  mutationFn: (vars: TVars) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (!projectId) return;
      void queryClient.invalidateQueries({ queryKey: crewKeys.roster(projectId) });
      void queryClient.invalidateQueries({ queryKey: crewKeys.periods(projectId) });
    },
  });
}

export function useAddCrewMember(projectId: string | undefined) {
  return useCrewMutation(projectId, async (input: AddCrewMemberInput) => {
    const res = await api.crew.add({ params: { id: projectId! }, body: input });
    if (res.status !== 201) throw new Error(errorMessage(res));
    return res.body.person;
  });
}

export function useOffboard(projectId: string | undefined) {
  return useCrewMutation(projectId, async (personId: string) => {
    const res = await api.crew.offboard({
      params: { id: projectId!, personId },
      body: {},
    });
    if (res.status !== 200) throw new Error(errorMessage(res));
    return res.body.person;
  });
}

export function useReonboard(projectId: string | undefined) {
  return useCrewMutation(projectId, async (personId: string) => {
    const res = await api.crew.reonboard({
      params: { id: projectId!, personId },
      body: {},
    });
    if (res.status !== 200) throw new Error(errorMessage(res));
    return res.body.person;
  });
}

export function useConfirmCertificate(projectId: string | undefined) {
  return useCrewMutation(
    projectId,
    async (vars: {
      personId: string;
      documentId: string;
      input: ConfirmCertificateInput;
    }) => {
      const res = await api.crew.confirmDocument({
        params: { personId: vars.personId, docId: vars.documentId },
        body: vars.input,
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.certificate;
    },
  );
}

export function useDeleteCertificate(projectId: string | undefined) {
  return useCrewMutation(
    projectId,
    async (vars: { personId: string; documentId: string }) => {
      const res = await api.crew.deleteDocument({
        params: { personId: vars.personId, docId: vars.documentId },
      });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
  );
}

// ------------------------------------------------------------------
// The upload machine
// ------------------------------------------------------------------

export type ScanUpload =
  | { phase: "idle" }
  | { phase: "uploading"; filename: string; progress: number }
  | { phase: "reading"; filename: string }
  | {
      phase: "ready";
      filename: string;
      documentId: string;
      suggestion: CertificateSuggestion;
    }
  | {
      phase: "error";
      message: string;
      file: File;
      /** Refused, and would be refused again: no "Try again" (`architecture/0031`). */
      refused: boolean;
    };

/**
 * Take one file through presign → PUT → complete, and stop at the form.
 *
 * A failure leaves a retryable state carrying the original `File` rather than
 * clearing it: the difference between "that didn't work, try again" and the
 * file silently vanishing from a drawer the user is still looking at.
 */
export function useCertificateScan(personId: string | undefined) {
  const [state, setState] = useState<ScanUpload>({ phase: "idle" });

  const reset = useCallback(() => setState({ phase: "idle" }), []);

  const upload = useCallback(
    async (type: DocType, file: File): Promise<void> => {
      if (!personId) return;
      setState({ phase: "uploading", filename: file.name, progress: 0 });

      try {
        // The API checks a certificate by the document rules, so the same
        // check runs here, before any request.
        const refused = documentRefusal(file);
        if (refused) throw new UploadRefused(refused);
        const presigned = await api.crew.uploadUrl({
          params: { personId },
          body: {
            type,
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
            sizeBytes: file.size,
          },
        });
        if (isRefusal(presigned.status)) throw new UploadRefused(errorMessage(presigned));
        if (presigned.status !== 201) throw new Error(errorMessage(presigned));

        await putWithProgress(presigned.body.uploadUrl, file, (progress) =>
          setState({ phase: "uploading", filename: file.name, progress }),
        );

        setState({ phase: "reading", filename: file.name });

        const completed = await api.crew.completeUpload({
          params: { personId, docId: presigned.body.documentId },
          body: {},
        });
        if (isRefusal(completed.status)) throw new UploadRefused(errorMessage(completed));
        if (completed.status !== 200) throw new Error(errorMessage(completed));

        setState({
          phase: "ready",
          filename: file.name,
          documentId: presigned.body.documentId,
          suggestion: completed.body.suggestion,
        });
      } catch (error) {
        const { error: message, refused } = uploadFailure(error);
        setState({ phase: "error", message, file, refused });
      }
    },
    [personId],
  );

  return { state, upload, reset };
}
